// COPY of nano-fibre-fsm/client/src/screens/AbsenceReporting/absenceReportingFlow.js
// — the same questions, options and branching, so a record edited on the
// HR dashboard (AbsenceRecordEditor.jsx) is asked exactly what FSM asks.
// Change a question in FSM's copy? Make the same change here.
//
// Absence Reporting & Return to Work — question graph transcribed
// word-for-word from the list supplied directly (feature/absence-reporting-
// and-return-to-work), plus the "Area" dropdown options transcribed from
// the supplied screenshot. Every question/option string below is copied
// exactly as given.
//
// Unlike the JUP SSRA form, this one has real branching: Q4 sends the
// engineer down one of three distinct paths (Report Late / Report Absence /
// Return to Work), each ending in its own submit rather than falling
// through to the next section header — so this uses the general SSRA's
// per-node/per-option `next` graph, not JUP's auto-linear wiring.
//
// One inferred completion, flagged here since it has no explicit "go to end
// of form" like its sibling branches do (N6 and N8 both say so explicitly):
//   - N14 "Absence Reporting Procedure Followed?" is the last question in
//     the Report Absence branch before the next section header ("Return to
//     Work"). Since Return to Work is a separate branch reached only via
//     Q4, not a continuation, N14 submits the form for both Yes and No —
//     implemented that way here, flagged for the document owner to confirm.
//
// Date questions (N16-N18) use a 'date' node type rendered by the
// components/DatePicker.jsx calendar popup — not a native
// <input type="date">, which is locale-formatted and stores an ISO value.
// DatePicker both displays and produces a literal 'dd/MM/yyyy' string, per
// instruction.

export const START_NODE = 'N1';
export const SUBMIT = 'SUBMIT';

export const SECTIONS = {
  EMPLOYEE_DETAILS: 'Employee Details',
  REPORT_LATE: 'Report Late',
  REPORT_ABSENCE: 'Report Absence',
  RETURN_TO_WORK: 'Return to Work',
};

const S = SECTIONS;

export const NODES = {
  N1: {
    section: S.EMPLOYEE_DETAILS,
    text: 'Employee Email',
    // Picked from the company directory (see AbsenceReportingForm.jsx's
    // QuestionField) rather than typed — every downstream lookup
    // (directory name, supervisor, the main dash's own rows)
    // matches on this email exactly, so a typo here silently breaks all
    // of them rather than erroring anywhere visible.
    type: 'employeeEmail',
    field: 'employeeEmail',
    next: 'N2',
  },
  // One Department/Area question (2026-09-28) — it used to be Department/Area
  // (Region B / Region N / Office...) followed by a separate Area question.
  // The regions are named in full here, and everything that isn't a region
  // is an Office option. Same list as FSM's copy of this flow.
  N2: {
    section: S.EMPLOYEE_DETAILS,
    text: 'Department/Area',
    type: 'select',
    field: 'departmentArea',
    options: [
      'Region B Gr. Manchester',
      'Region B Lancashire',
      'Region B Midlands',
      'Region B St. Helens',
      'Region B Yorkshire',
      'Region N Bristol',
      'Region N East Midlands',
      'Region N Gr. Manchester',
      'Region N Merseyside',
      'Region N Oxford',
      'Region N Preston',
      'Region N SouthEast',
      'Region N The East',
      'Region N West Midlands',
      'Region N Yorkshire',
      'Office - Stores',
      'Office - Fleet',
      'Office - Logistics',
      'Office - Operations',
      'Office - Finance',
    ].map((label) => ({ label, next: 'N4' })),
  },
  // No longer asked — merged into Department/Area above. Kept so an older
  // record that has an Area still opens in the editor; the area column stays.
  N3: {
    section: S.EMPLOYEE_DETAILS,
    text: 'Area',
    type: 'select',
    field: 'area',
    options: [
      { label: 'Engineering - Southampton', next: 'N4' },
      { label: 'Office - Logistics', next: 'N4' },
      { label: 'Office - Operations', next: 'N4' },
      { label: 'Office - Finance', next: 'N4' },
      { label: 'Warehouse - Stock Team', next: 'N4' },
    ],
  },
  // Hidden: which branch this is is now decided one step earlier, by which
  // category row the reporter picked on the Absence Reporting & Return to
  // Work screen (AbsenceReportingCategory.jsx) — reportType always arrives
  // prefilled via query param before this form renders (see
  // AbsenceReportingForm.jsx's redirect-to-/absence-reporting guard for the
  // case where it doesn't). Kept as a real node, not deleted, because
  // getNextNodeId still branches on its `field` — just not rendered as a
  // question.
  N4: {
    section: S.EMPLOYEE_DETAILS,
    text: 'Report Absence/Return to Work',
    type: 'select',
    field: 'reportType',
    hidden: true,
    options: [
      { label: 'Report Absence', next: 'N7' },
      { label: 'Report Late', next: 'N5' },
      { label: 'Return to Work', next: 'N16' },
    ],
  },

  // --- Report Late ---
  N5: {
    section: S.REPORT_LATE,
    text: 'Employee Time of Arrival',
    // Native time-scroll/clock picker (see AbsenceReportingForm.jsx's
    // QuestionField) — the old free-text version needed a helper telling
    // people the expected "08:30, 09:30 etc." format; the picker only ever
    // produces that same HH:mm value, so there's nothing left to explain.
    type: 'time',
    field: 'employeeTimeOfArrival',
    next: 'N6',
  },
  N6: {
    section: S.REPORT_LATE,
    text: 'Reason for late',
    type: 'textarea',
    field: 'reasonForLate',
    next: SUBMIT,
  },

  // --- Report Absence ---
  // Hidden, and always answered 'No' by AbsenceReportingForm.jsx before
  // render: this branch is reached only via the "raise a new absence"
  // action button, never as a continuation of one already on file, so
  // there is nothing to ask here. N8 (the RTWID for a continuation) is
  // consequently never reached and needs no change of its own.
  N7: {
    section: S.REPORT_ABSENCE,
    text: 'Is this an ongoing absence?',
    helperText: 'If this is a continuation of a prior absence please select yes and provide the ID Number',
    type: 'select',
    field: 'ongoingAbsence',
    hidden: true,
    options: [
      { label: 'Yes', next: 'N8' },
      { label: 'No', next: 'N9' },
    ],
  },
  N8: {
    section: S.REPORT_ABSENCE,
    text: 'Existing ID (RTWID)',
    helperText: "Found in Nano Management > General > RTW's Due",
    type: 'text',
    field: 'existingRtwId',
    next: SUBMIT,
  },
  N9: {
    section: S.REPORT_ABSENCE,
    text: 'Absence Reporting Time',
    helperText: 'If the employee reports to work (Site/Office) please select "Throughout Day".',
    type: 'select',
    field: 'absenceReportingTime',
    options: [
      { label: 'Start of Day', next: 'N11' },
      { label: 'Throughout Day', next: 'N10' },
    ],
  },
  N10: {
    section: S.REPORT_ABSENCE,
    text: 'Time Employee left site/work',
    type: 'time',
    field: 'timeEmployeeLeftSite',
    next: 'N11', // inferred — no explicit "go to" given
  },
  N11: {
    section: S.REPORT_ABSENCE,
    text: 'Absence Category',
    type: 'select',
    forceDropdown: true,
    field: 'absenceCategory',
    options: [
      { label: 'Sickness', next: 'N12' },
      { label: 'AWOL', next: 'N12' },
      { label: 'Bereavement', next: 'N12' },
      { label: 'Unauthorised absence', next: 'N12' },
      { label: 'Unplanned absence', next: 'N12' },
    ],
  },
  N12: {
    section: S.REPORT_ABSENCE,
    text: 'Reason for Absence',
    type: 'textarea',
    field: 'reasonForAbsence',
    next: 'N13', // inferred — no explicit "go to" given
  },
  N13: {
    section: S.REPORT_ABSENCE,
    text: 'Reporting Method',
    type: 'select',
    field: 'reportingMethod',
    options: [
      { label: 'Call Before 08:00', next: 'N14' },
      { label: 'No Call Before 08:00 (Text, Email, Other)', next: 'N14' },
    ],
  },
  N14: {
    section: S.REPORT_ABSENCE,
    text: 'Absence Reporting Procedure Followed?',
    type: 'select',
    field: 'absenceReportingProcedureFollowed',
    options: [
      { label: 'Yes', next: SUBMIT }, // see file header
      { label: 'No', next: SUBMIT },
    ],
  },

  // --- Return to Work ---
  N16: {
    section: S.RETURN_TO_WORK,
    text: 'Meeting Date',
    helperText: "The date you're meeting with the employee to complete this Return to Work. Please input date (dd/MM/yyyy)",
    type: 'date',
    field: 'meetingDate',
    next: 'N17',
  },
  N17: {
    section: S.RETURN_TO_WORK,
    text: 'Absence Start Date',
    helperText: 'Please input date (dd/MM/yyyy)',
    type: 'date',
    field: 'absenceStartDate',
    next: 'N18',
  },
  N18: {
    section: S.RETURN_TO_WORK,
    text: 'Absence End Date',
    helperText: 'Please input date (dd/MM/yyyy)',
    type: 'date',
    field: 'absenceEndDate',
    next: 'N16B',
  },
  // Computed, not asked — AbsenceReportingForm.jsx fills this in itself,
  // straight from Absence Start Date and Absence End Date above (inclusive
  // of both ends). Placed after them, not before, so both are already
  // answered by the time it has anything to calculate from. Previously
  // looked up the employee's original absence report and counted to today
  // instead — dropped because that lookup could fail independently of
  // anything the manager typed, leaving Days Absent stuck with no way to
  // fix it from the form itself.
  N16B: {
    section: S.RETURN_TO_WORK,
    text: 'Days Absent',
    helperText: 'Calculated automatically from Absence Start Date and Absence End Date above.',
    type: 'computed',
    field: 'daysAbsent',
    next: 'N20',
  },
  // N19 ("Number of absence occasions in the last 12 months") removed —
  // supervisors don't have that history to hand. absence_occasions_last_12_months
  // stays in the schema unused, same as record_id/workflow_id on the SSRA
  // forms, so no migration is needed.
  N20: {
    section: S.RETURN_TO_WORK,
    text: 'Ask the employee for a reason for their absence and note it below',
    type: 'textarea',
    field: 'employeeAbsenceReasonNote',
    next: 'N21',
  },
  N21: {
    section: S.RETURN_TO_WORK,
    text: 'Is the employee fully fit to conduct all their duties?',
    type: 'select',
    field: 'fullyFitForDuties',
    options: [
      { label: 'Yes', next: 'N21B' },
      { label: 'No', next: 'N22' },
    ],
  },
  // A Yes answer asks for a reason before moving on; No skips straight to
  // the next question — same pattern for every Yes/No question in this
  // branch below.
  N21B: {
    section: S.RETURN_TO_WORK,
    text: 'Please provide a reason',
    type: 'textarea',
    field: 'fullyFitForDutiesReason',
    next: 'N22',
  },
  N22: {
    section: S.RETURN_TO_WORK,
    text: 'Is the absence due to an accident at work?',
    type: 'select',
    field: 'absenceDueToAccidentAtWork',
    options: [
      { label: 'Yes', next: 'N22B' },
      { label: 'No', next: 'N23' },
    ],
  },
  N22B: {
    section: S.RETURN_TO_WORK,
    text: 'Please provide a reason',
    type: 'textarea',
    field: 'absenceDueToAccidentAtWorkReason',
    next: 'N23',
  },
  N23: {
    section: S.RETURN_TO_WORK,
    text: 'Is there likely to be any further absences in relation to this condition?',
    type: 'select',
    field: 'furtherAbsencesLikely',
    options: [
      { label: 'Yes', next: 'N23B' },
      { label: 'No', next: 'N24' },
    ],
  },
  N23B: {
    section: S.RETURN_TO_WORK,
    text: 'Please provide a reason',
    type: 'textarea',
    field: 'furtherAbsencesLikelyReason',
    next: 'N24',
  },
  N24: {
    section: S.RETURN_TO_WORK,
    text: 'Were the proper absence reporting procedures followed?',
    type: 'select',
    field: 'properProceduresFollowed',
    // Yes needs no explanation — procedures were followed, nothing to add —
    // so it goes straight on; only No asks for the acknowledgement (N25).
    // Same change as FSM's own copy of this flow.
    options: [
      { label: 'Yes', next: 'N26' },
      { label: 'No', next: 'N25' },
    ],
  },
  // No longer reached (Yes skips it, 2026-09-28). Kept so a draft saved while
  // on this question still opens; the column stays for earlier reports' reasons.
  N24B: {
    section: S.RETURN_TO_WORK,
    text: 'Please provide a reason',
    type: 'textarea',
    field: 'properProceduresFollowedReason',
    next: 'N26',
  },
  N25: {
    section: S.RETURN_TO_WORK,
    text: 'Tick to confirm that you have explained the proper absence reporting procedures to the Employee',
    type: 'ack',
    field: 'procedureExplainedAck',
    options: [{ label: 'Absence reporting procedures were explained and understood by Employee', next: 'N26' }],
  },
  N26: {
    section: S.RETURN_TO_WORK,
    text: 'Are there any patterns/reoccurring reasons of employee absence? if so, what employee is doing to prevent that?',
    type: 'textarea',
    field: 'absencePatternsNote',
    next: 'N27',
  },
  N27: {
    section: S.RETURN_TO_WORK,
    text: 'Has the employee sought doctor/ hospital advice?',
    type: 'select',
    field: 'soughtMedicalAdvice',
    options: [
      { label: 'Yes', next: 'N28' },
      { label: 'No', next: SUBMIT },
    ],
  },
  N28: {
    section: S.RETURN_TO_WORK,
    text: 'What medical advice did the Employee receive?',
    type: 'textarea',
    field: 'medicalAdviceReceived',
    next: 'N29', // inferred — no explicit "go to" given
  },
  N29: {
    section: S.RETURN_TO_WORK,
    text: 'Can this treatment/medication affect the employee at work?',
    type: 'select',
    field: 'treatmentAffectsWork',
    options: [
      { label: 'Yes', next: 'N29B' },
      { label: 'No', next: SUBMIT },
    ],
  },
  N29B: {
    section: S.RETURN_TO_WORK,
    text: 'Please provide a reason',
    type: 'textarea',
    field: 'treatmentAffectsWorkReason',
    next: SUBMIT,
  },
};

// Pure function: given the current node and the value just answered, returns
// the id of the next node (or SUBMIT).
export function getNextNodeId(nodeId, value) {
  const node = NODES[nodeId];
  if (node.next) return node.next; // text/textarea/date — single path
  const match = node.options.find((opt) => opt.label === value);
  return match ? match.next : null;
}

export function isAnswered(node, answers) {
  const value = answers[node.field];
  if (node.type === 'text' || node.type === 'textarea' || node.type === 'date') {
    return typeof value === 'string' && value.trim() !== '';
  }
  return value !== undefined && value !== null && value !== ''; // select / ack
}

// Same pattern as the general SSRA's getVisiblePath — a pure function of the
// current answers, re-run on every render, walking the graph from
// START_NODE and stopping at the first unanswered question. Real branching
// (Report Late / Report Absence / Return to Work) means only the section(s)
// on the path the engineer actually took ever appear.
export function getVisiblePath(answers) {
  const path = [];
  let currentId = START_NODE;
  let terminal = null;
  let guard = 0;
  while (currentId && NODES[currentId] && guard++ < 200) {
    const node = NODES[currentId];
    path.push(currentId);
    if (!isAnswered(node, answers)) break;
    const nextId = getNextNodeId(currentId, answers[node.field]);
    if (nextId === SUBMIT) {
      terminal = nextId;
      break;
    }
    currentId = nextId;
  }
  return { path, terminal };
}

export function groupBySection(nodeIds) {
  const groups = [];
  for (const nodeId of nodeIds) {
    const section = NODES[nodeId].section;
    let group = groups.find((g) => g.section === section);
    if (!group) {
      group = { section, nodeIds: [] };
      groups.push(group);
    }
    group.nodeIds.push(nodeId);
  }
  return groups;
}
